import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import CloseIcon from "@mui/icons-material/Close";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import RedeemOutlinedIcon from "@mui/icons-material/RedeemOutlined";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import VolunteerActivismOutlinedIcon from "@mui/icons-material/VolunteerActivismOutlined";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import { useHistory, useParams } from "react-router-dom";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import PromoStoreAddonCard, { PROMO_STORE_ADDON_STATUS_LABELS, PromoStoreAddonIcon } from "../../../../components/PromoStoreAddonCard";
import { UITableToolbar } from "../../../../components/ui";
import PromoStoreNavigation from "./PromoStoreNavigation";
import { fetchPromoStoreAddonCatalog } from "../../../../services/promoStoreAddons";
import "./InstancePromoStores.css";

// Marketplace hero artwork — replace with final PromoStore marketplace banner asset.
const MARKETPLACE_HERO_ARTWORK = "https://elitecore.ca/wp-content/uploads/2026/09/elite-core-promostores-marketplace-promotional-image.png";

const CATEGORY_OPTIONS = [
  { key: "featured", label: "Featured", icon: AutoAwesomeOutlinedIcon, color: "#7c3aed", soft: "#ede9fe" },
  { key: "games", label: "Games", icon: CelebrationOutlinedIcon, color: "#db2777", soft: "#fce7f3" },
  { key: "recognition", label: "Recognition", icon: VolunteerActivismOutlinedIcon, color: "#e11d48", soft: "#ffe4e6" },
  { key: "employee-programs", label: "Employee Programs", icon: GroupsOutlinedIcon, color: "#2563eb", soft: "#dbeafe" },
  { key: "sales-incentives", label: "Sales Incentives", icon: EmojiEventsOutlinedIcon, color: "#d97706", soft: "#fef3c7" },
  { key: "events", label: "Events", icon: EventOutlinedIcon, color: "#0891b2", soft: "#cffafe" },
  { key: "store-enhancements", label: "Store Enhancements", icon: StorefrontOutlinedIcon, color: "#15803d", soft: "#dcfce7" },
  { key: "hr-culture", label: "HR & Culture", icon: RedeemOutlinedIcon, color: "#c2410c", soft: "#ffedd5" },
  { key: "rewards-employee-programs", label: "Rewards & Employee Programs", icon: GroupsOutlinedIcon, color: "#2563eb", soft: "#dbeafe" },
  { key: "store-experience", label: "Store Experience", icon: StorefrontOutlinedIcon, color: "#15803d", soft: "#dcfce7" },
  { key: "products-inventory", label: "Products & Inventory", icon: ViewModuleOutlinedIcon, color: "#0f766e", soft: "#ccfbf1" },
  { key: "operations-ordering", label: "Operations & Ordering", icon: InsightsOutlinedIcon, color: "#4f46e5", soft: "#e0e7ff" },
  { key: "integrations-apis", label: "Integrations & APIs", icon: AutoAwesomeOutlinedIcon, color: "#7c3aed", soft: "#ede9fe" },
  { key: "analytics", label: "Analytics", icon: InsightsOutlinedIcon, color: "#4f46e5", soft: "#e0e7ff" },
  { key: "all", label: "All Add-Ons", icon: ViewModuleOutlinedIcon, color: "#475569", soft: "#e2e8f0" },
];

const MarketplaceCategoryBar = ({ catalogCategories, selected, onSelect }) => {
  const supported = new Set((catalogCategories || []).map(({ key }) => key));
  return (
    <Box component="nav" aria-label="Marketplace categories" sx={{ display: "flex", gap: 1.25, overflowX: "scroll", py: 2, px: 0.5, scrollbarWidth: "thin", WebkitOverflowScrolling: "touch" }}>
      {CATEGORY_OPTIONS.map(({ key, label, icon: CategoryIcon, color, soft }) => {
        const available = key === "featured" || key === "all" || supported.has(key);
        const button = (
          <Button
            key={key}
            aria-pressed={available ? selected === key : undefined}
            aria-disabled={!available}
            onClick={() => available && onSelect(key)}
            sx={{
              flex: "0 0 auto", minWidth: 112, minHeight: 90, px: 1.5, py: 1.25, borderRadius: 2.5,
              border: "1px solid", borderColor: selected === key ? color : "#e5e7eb", bgcolor: selected === key ? soft : "#fff",
              color: available ? "text.primary" : "text.disabled", textTransform: "none", cursor: available ? "pointer" : "not-allowed",
              display: "flex", flexDirection: "column", gap: 0.75, "&:hover": { bgcolor: available ? soft : "#fff", borderColor: available ? color : "#e5e7eb" },
              "&:focus-visible": { outline: `3px solid ${soft}`, outlineOffset: 2 },
            }}
          >
            <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: soft, color, display: "grid", placeItems: "center" }}><CategoryIcon fontSize="small" /></Box>
            <Typography variant="caption" fontWeight={800} sx={{ whiteSpace: "nowrap" }}>{label}</Typography>
          </Button>
        );
        return available ? button : <Tooltip key={key} title="Coming soon" arrow><span>{button}</span></Tooltip>;
      })}
    </Box>
  );
};

const MarketplaceHero = () => (
  <Box component="section" sx={{ position: "relative", width: "100%", isolation: "isolate", overflow: "hidden", minHeight: { xs: 390, sm: 350, md: 420 }, bgcolor: "#80131a", color: "#fff", display: "flex", alignItems: "center", boxShadow: "0 14px 40px rgba(88, 15, 20, 0.18)" }}>
    <Box aria-hidden="true" sx={{ position: "absolute", width: "100%", inset: 0, zIndex: -2, backgroundImage: `url(${MARKETPLACE_HERO_ARTWORK})`, backgroundSize: "100%", backgroundRepeat: "no-repeat", backgroundPosition: { xs: "right bottom", sm: "right bottom", md: "right bottom" } }} />
    <Box aria-hidden="true" sx={{ position: "absolute", width: "100%", inset: 0, zIndex: -1, background: { xs: "linear-gradient(90deg, rgb(0 0 0 / 58%) 0%, rgb(0 0 0 / 44%) 48%, rgb(3 12 13 / 30%) 60%, rgba(110, 12, 20, .08) 70%)" } }} />
    <Box sx={{ width: { xs: "100%", sm: "58%", md: "44%" }, p: { xs: 3, sm: 4, md: 5 } }}>
      <Typography component="h1" sx={{ fontSize: { xs: "2.2rem", sm: "2.7rem", md: "3.25rem" }, lineHeight: 1.05, letterSpacing: "-0.035em", fontWeight: 900 }}>
        Turn Moments Into<br />Meaningful Engagement
      </Typography>
      <Typography sx={{ mt: 2, maxWidth: 480, fontSize: { xs: "1rem", md: "1.12rem" }, lineHeight: 1.7, color: "rgba(255,255,255,.92)" }}>
        Add powerful experiences to your PromoStore.<br />Games, rewards, recognition, and more - all in one place.
      </Typography>
    </Box>
  </Box>
);

const MarketplaceDetailsDialog = ({ addon, onClose }) => {
  const status = addon ? PROMO_STORE_ADDON_STATUS_LABELS[addon.status] || addon.status : "";
  const contactOnly = addon?.status === "contact" || addon?.status === "custom";
  return (
    <Dialog open={!!addon} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: { xs: 2, sm: 3 }, m: { xs: 1.5, sm: 3 } } }}>
      <DialogContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        <IconButton aria-label="Close add-on details" onClick={onClose} sx={{ position: "absolute", top: 10, right: 10 }}><CloseIcon /></IconButton>
        {addon ? <Stack spacing={2.5}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", pr: 4 }}><PromoStoreAddonIcon definition={addon} /><Box><Typography component="h2" variant="h5" fontWeight={900}>{addon.name}</Typography><Stack direction="row" gap={1} mt={1} flexWrap="wrap"><Chip size="small" label={addon.category?.name} /><Chip size="small" color={addon.status === "available" ? "success" : "default"} label={status} /></Stack></Box></Box>
          <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>{addon.description}</Typography>
          {addon.features?.length ? <Box><Typography component="h3" fontWeight={800}>Capabilities</Typography><Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>{addon.features.map((feature) => <li key={feature}><Typography variant="body2" sx={{ mb: 0.75 }}>{feature}</Typography></li>)}</Box></Box> : null}
          {addon.useCases?.length ? <Box><Typography component="h3" fontWeight={800}>Great for</Typography><Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>{addon.useCases.map((useCase) => <li key={useCase}><Typography variant="body2" sx={{ mb: 0.75 }}>{useCase}</Typography></li>)}</Box></Box> : null}
          {addon.status === "coming-soon" ? <Alert severity="info">Coming Soon — this capability is not yet available.</Alert> : null}
          {contactOnly ? <Alert severity="info">Contact Elite Promo Marketing to explore a custom program for your team.</Alert> : null}
        </Stack> : null}
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2.5, sm: 3.5 }, pb: 2.5 }}><Button onClick={onClose} variant="contained">Close</Button></DialogActions>
    </Dialog>
  );
};

const PromoStoreMarketplace = () => {
  const { instanceUniqueId } = useParams();
  const history = useHistory();
  const [catalog, setCatalog] = useState({ data: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("featured");
  const [selectedAddon, setSelectedAddon] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchPromoStoreAddonCatalog()
      .then((response) => { if (active) setCatalog(response || { data: [], categories: [] }); })
      .catch((requestError) => { if (active) setError(requestError?.response?.data?.message || "Unable to load the Add-On Marketplace."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (catalog.data || []).filter((addon) => {
      const matchesCategory = category === "all" || (category === "featured" ? addon.featured : addon.category?.key === category);
      const haystack = `${addon.name} ${addon.description} ${addon.category?.name}`.toLowerCase();
      return matchesCategory && (!query || haystack.includes(query));
    });
  }, [catalog.data, category, search]);

  return (
    <div className="instance-dashboard instance-webstores-page instance-webstores-page-home">
      <InstanceLeftSidebar />
      <div className="rightSide">
        <div className="options-container fade-in-effect-usability"><div className="options-inner-container">
          <UITableToolbar
            className="promostores-toolbar"
            sx={{
              p: 0,
              backgroundColor: "background.paper",
              borderColor: "divider",
              borderTop: "3px solid",
              borderLeft: 0,
              borderRight: 0,
              borderBottom: 0,
              borderTopColor: "primary.main",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ px: 2.5, py: 1.75 }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: "text.primary", lineHeight: 1 }}>
                PromoStores
              </Typography>
              <Box sx={{ height: 20, borderLeft: "1px solid", borderColor: "divider" }} />
              <PromoStoreNavigation
                items={[
                  { key: "stores", label: "All Stores" },
                  { key: "marketplace", label: "Marketplace" },
                ]}
                activeKey="marketplace"
                onSelect={(key) => {
                  if (key === "stores") history.push(`/instance/${instanceUniqueId}/sales/promostores`);
                }}
              />
            </Stack>
          </UITableToolbar>
          <Box sx={{ width: "100%", bgcolor: "#f7f8fa", minHeight: "calc(100vh - 120px)" }}>
            <MarketplaceHero />
            <MarketplaceCategoryBar catalogCategories={catalog.categories} selected={category} onSelect={setCategory} />
            <Box component="section" aria-labelledby="marketplace-section-heading" sx={{ mt: 1, padding: "30px", boxSizing: "border-box" }}>
              <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: { md: "flex-end" }, gap: 2, mb: 2.5 }}>
                <Box><Typography id="marketplace-section-heading" component="h2" variant="h5" fontWeight={900}>{category === "all" ? "All Add-Ons" : category === "featured" ? "Featured Add-Ons" : CATEGORY_OPTIONS.find(({ key }) => key === category)?.label}</Typography><Typography color="text.secondary" sx={{ mt: 0.5 }}>Explore ways to engage, reward and motivate your team.</Typography></Box>
                <TextField size="small" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search add-ons..." inputProps={{ "aria-label": "Search add-ons" }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} sx={{ width: { xs: "100%", md: 300 }, bgcolor: "#fff" }} />
              </Box>
              {loading ? <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress /><Typography sx={{ mt: 1 }}>Loading Marketplace add-ons…</Typography></Box> : null}
              {error ? <Alert severity="error">{error}</Alert> : null}
              {!loading && !error && !(catalog.data || []).length ? <Alert severity="info">No Marketplace add-ons are available yet.</Alert> : null}
              {!loading && !error && (catalog.data || []).length > 0 && !visible.length ? <Alert severity="info">No add-ons match your search. Try another search or browse all Marketplace categories.</Alert> : null}
              {!loading && !error && visible.length ? <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))", xl: "repeat(5, minmax(0, 1fr))" }, gap: { xs: 1.5, md: 2 } }}>{visible.map((addon) => <PromoStoreAddonCard key={addon.key} definition={addon} onViewDetails={() => setSelectedAddon(addon)} />)}</Box> : null}
            </Box>
          </Box>
        </div></div>
      </div>
      <MarketplaceDetailsDialog addon={selectedAddon} onClose={() => setSelectedAddon(null)} />
    </div>
  );
};

export default PromoStoreMarketplace;
